// signals-web.ts — the WEB runtime's derived signals for ONE vehicle.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts. On web the whole
// InsightsPanel (fuel economy, cost per distance, monthly spend, projection — with trend arrows)
// rendered zeros, and the billed narration + Ask were handed an empty logbook.
// Native is unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// ⚠️ THE CEILING TRAVELS WITH THE DATA. signals.rs says it in its own words: "purely observational
// — never 'due', never 'you should', never a diagnosis." That is why `ServiceInterval` reports
// `longer_than_usual` as a RATIO against the owner's OWN historical gap rather than a reminder.
// This app tells someone facts about their car; it must never imply their brakes need doing.
// Keep every string here in that voice.

import type { Vehicle, Fillup, Service, GarageSignals } from "./store";

const RECENT_WINDOW = 5;     // economy segments in the "recent" moving average
const RECENT_DAYS = 365;     // last 12 months
const PRIOR_DAYS = 730;      // the 12 before that, for the spend delta

const round2 = (x: number) => Math.round(x * 100) / 100;
const round3 = (x: number) => Math.round(x * 1000) / 1000;
const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

const parseTs = (ts: string): Date | null => {
  const t = Date.parse(ts ?? "");
  return Number.isNaN(t) ? null : new Date(t);
};
const dayOf = (ts: string) => { const d = parseTs(ts); return d ? d.toISOString().slice(0, 10) : (ts ?? "").slice(0, 10); };
const daysBetween = (a: string, b: string) =>
  Math.max(0, Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000));
const shiftDays = (isoDay: string, back: number) =>
  new Date(Date.parse(`${isoDay}T00:00:00Z`) - back * 86400000).toISOString().slice(0, 10);

/** Mirror of signals.rs::slope — least-squares gradient over the segment index. */
function slope(ys: number[]): number | null {
  const n = ys.length;
  if (n < 2) return null;
  const xs = ys.map((_, i) => i);
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) { num += (xs[i] - mx) * (ys[i] - my); den += (xs[i] - mx) ** 2; }
  return den === 0 ? 0 : num / den;
}

/** Mirror of signals.rs::pretty_kind ("oil-change" → "Oil change"). */
const prettyKind = (k: string) => { const s = (k ?? "").replace(/-/g, " "); return s ? s[0].toUpperCase() + s.slice(1) : ""; };

/** Mirror of the `fmt0` closure — integer with thousands separators. */
const fmt0 = (x: number) => Math.round(x).toLocaleString("en-US", { maximumFractionDigits: 0 });

/** Mirror of signals.rs::build_label. */
function buildLabel(v: Vehicle): string {
  const parts = [v.year > 0 ? String(v.year) : "", v.make, v.model].filter(Boolean);
  return parts.length ? `${v.name} (${parts.join(" ")})` : v.name;
}

interface EconomyPoint { date: string; distance: number; volume: number; economy: number; cost: number }

export function computeSignalsWeb(
  vehicle: Vehicle, fillups: Fillup[], services: Service[], todayStr: string,
): GarageSignals {
  const economyUnit = vehicle.distance_unit === "km" ? "km/L" : "mi/gal";
  const cpdUnit = `cost per ${vehicle.distance_unit}`;

  // Fill-ups by odometer ascending, ties broken by timestamp — the order the segment maths needs.
  const fills = [...fillups].sort((a, b) => a.odometer - b.odometer || a.ts.localeCompare(b.ts));

  // ── Overall ────────────────────────────────────────────────────────────────
  const firstOdo = fills.length ? fills[0].odometer : null;
  const latestOdo = fills.length ? fills[fills.length - 1].odometer : null;
  const times = fills.map((f) => parseTs(f.ts)).filter((d): d is Date => d !== null).map((d) => d.getTime());
  const firstDate = times.length ? new Date(Math.min(...times)) : null;
  const latestDate = times.length ? new Date(Math.max(...times)) : null;
  const overall = {
    fillups: fills.length,
    services: services.length,
    first_odometer: firstOdo,
    latest_odometer: latestOdo,
    distance_tracked: firstOdo !== null && latestOdo !== null && latestOdo > firstOdo ? round2(latestOdo - firstOdo) : null,
    first_date: firstDate ? firstDate.toISOString().slice(0, 10) : null,
    latest_date: latestDate ? latestDate.toISOString().slice(0, 10) : null,
    days_tracked: firstDate && latestDate ? Math.max(0, Math.round((latestDate.getTime() - firstDate.getTime()) / 86400000)) : 0,
  };

  // ── Fuel economy: segments between FULL tanks ──────────────────────────────
  // A segment runs from one full tank to the next; partial fills in between roll their volume and
  // cost forward into the open segment. A partial-only stretch never produces a reading, which is
  // why "Need 2 full-tank fills" is the honest empty state rather than a made-up number.
  const points: EconomyPoint[] = [];
  {
    let open = false, startOdo = 0, vol = 0, cost = 0;
    for (const f of fills) {
      if (open) {
        vol += f.volume; cost += f.cost;
        if (f.is_full_tank) {
          const distance = f.odometer - startOdo;
          if (distance > 0 && vol > 0) {
            points.push({
              date: dayOf(f.ts),
              distance: round2(distance),
              volume: round3(vol),
              economy: round2(distance / vol),
              cost: round2(cost),
            });
          }
          startOdo = f.odometer; vol = 0; cost = 0;   // close and re-open from this tank
        }
      } else if (f.is_full_tank) {
        open = true; startOdo = f.odometer; vol = 0; cost = 0;
      }
    }
  }

  const economy = buildEconomy(points);
  const cost_per_distance = buildCpd(points, todayStr);
  const service = buildServiceStats(services, latestOdo, todayStr);
  const spend_by_category = buildSpendByCategory(services, todayStr);
  const spend = buildSpendSummary(fills, points, services, overall, todayStr);
  const projection = buildProjection(overall);

  const units = { economy: economyUnit, distance: vehicle.distance_unit };
  const headline_facts = buildHeadlineFacts(units, overall, economy, cost_per_distance, service, spend_by_category, spend, projection);

  return {
    schema:
      "oriora.garage.v2-signals: derived aggregates for ONE vehicle (no raw notes, no exact plate). Fuel economy is measured between FULL tanks only. Every figure is the owner's OWN logged data in their own units and currency. headline_facts = pre-ranked, ready-to-say observational sentences already containing the figures — lead with these, never invent a number. OBSERVATIONAL ONLY: state elapsed distance/time since a service as a FACT against the owner's own average; NEVER say a service is 'due' or 'overdue', never advise, never diagnose a fault.",
    generated_for: todayStr,
    vehicle: {
      name: vehicle.name,
      label: buildLabel(vehicle),
      distance_unit: vehicle.distance_unit,
      volume_unit: vehicle.volume_unit,
      economy_unit: economyUnit,
      cost_per_distance_unit: cpdUnit,
    },
    overall, economy, cost_per_distance, service, spend_by_category, spend, projection, headline_facts,
  } as unknown as GarageSignals;
}

/** Mirror of signals.rs::build_economy. */
function buildEconomy(points: EconomyPoint[]) {
  const eco = points.map((p) => p.economy);
  const totalDist = points.reduce((a, p) => a + p.distance, 0);
  const totalVol = points.reduce((a, p) => a + p.volume, 0);
  const lifetime_avg = totalVol > 0 ? round2(totalDist / totalVol) : null;

  let recent_avg: number | null, prior_avg: number | null;
  if (eco.length >= 2) {
    const cut = Math.max(0, eco.length - RECENT_WINDOW);
    const r = mean(eco.slice(cut));
    const p = cut === 0 ? null : mean(eco.slice(0, cut));
    recent_avg = r === null ? null : round2(r);
    prior_avg = p === null ? null : round2(p);
  } else {
    recent_avg = eco.length ? round2(eco[0]) : null;
    prior_avg = null;
  }

  let delta: number | null = null, direction: string | null = null;
  if (recent_avg !== null && prior_avg !== null) {
    delta = round2(recent_avg - prior_avg);
    direction = delta > 0.3 ? "improving" : delta < -0.3 ? "declining" : "flat";
  }

  const best = eco.length ? round2(Math.max(...eco)) : null;
  const worst = eco.length ? round2(Math.min(...eco)) : null;

  return {
    points,
    lifetime_avg, recent_avg, prior_avg, delta, direction,
    slope_per_fill: (() => { const s = slope(eco); return s === null ? null : round3(s); })(),
    best, worst,
    recent_vs_lifetime_pct: recent_avg !== null && lifetime_avg !== null && lifetime_avg > 0
      ? round2((recent_avg / lifetime_avg) * 100) : null,
    off_best_pct: recent_avg !== null && best !== null && best > 0
      ? Math.max(0, round2(((best - recent_avg) / best) * 100)) : null,
  };
}

/** Mirror of signals.rs::build_cpd — last 12 months vs the 12 before. */
function buildCpd(points: EconomyPoint[], today: string) {
  const recentCut = shiftDays(today, RECENT_DAYS);
  const priorCut = shiftDays(today, PRIOR_DAYS);
  let rD = 0, rC = 0, pD = 0, pC = 0, lD = 0, lC = 0;
  for (const pt of points) {
    lD += pt.distance; lC += pt.cost;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pt.date)) continue;
    if (pt.date >= recentCut) { rD += pt.distance; rC += pt.cost; }
    else if (pt.date >= priorCut) { pD += pt.distance; pC += pt.cost; }
  }
  const recent = rD > 0 ? round3(rC / rD) : null;
  const prior = pD > 0 ? round3(pC / pD) : null;
  const lifetime = lD > 0 ? round3(lC / lD) : null;
  let delta: number | null = null, pct_change: number | null = null, direction: string | null = null;
  if (recent !== null && prior !== null && prior > 0) {
    delta = round3(recent - prior);
    pct_change = round2(((recent - prior) / prior) * 100);
    direction = pct_change > 2 ? "up" : pct_change < -2 ? "down" : "flat";
  }
  return { recent, prior, delta, pct_change, direction, lifetime };
}

/** Mirror of signals.rs::build_service_stats. Ratios only — never a "due" verdict. */
function buildServiceStats(services: Service[], latestOdo: number | null, today: string) {
  const byKind = new Map<string, Service[]>();
  for (const s of services) byKind.set(s.kind, [...(byKind.get(s.kind) ?? []), s]);

  const intervals = [...byKind.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([kind, raw]) => {
    const evs = [...raw].sort((a, b) => a.odometer - b.odometer);
    const gapsDist = evs.length >= 2 ? evs.slice(1).map((e, i) => Math.max(0, e.odometer - evs[i].odometer)) : [];
    const avg_distance_between = gapsDist.length ? round2(mean(gapsDist) as number) : null;

    const tsorted = evs.map((s) => parseTs(s.ts)).filter((d): d is Date => d !== null).sort((a, b) => a.getTime() - b.getTime());
    const gapsDays = tsorted.length >= 2
      ? tsorted.slice(1).map((d, i) => Math.max(0, Math.round((d.getTime() - tsorted[i].getTime()) / 86400000))) : [];
    const avg_days_between = gapsDays.length ? round2(mean(gapsDays) as number) : null;

    const last = evs.reduce((m, s) => (s.ts > m.ts ? s : m), evs[0]);
    const last_date = dayOf(last.ts);
    const days_since_last = /^\d{4}-\d{2}-\d{2}$/.test(last_date) ? daysBetween(last_date, today) : 0;
    const distance_since_last = latestOdo !== null && latestOdo >= last.odometer ? round2(latestOdo - last.odometer) : null;

    const ratio_vs_own_avg_distance = distance_since_last !== null && avg_distance_between !== null && avg_distance_between > 0
      ? round2(distance_since_last / avg_distance_between) : null;
    const ratio_vs_own_avg_days = avg_days_between !== null && avg_days_between > 0
      ? round2(days_since_last / avg_days_between) : null;

    return {
      kind, count: evs.length,
      avg_distance_between, avg_days_between,
      last_date, last_odometer: last.odometer,
      distance_since_last, days_since_last,
      ratio_vs_own_avg_distance, ratio_vs_own_avg_days,
      // "Longer than the owner's OWN usual gap" — a comparison to their history, not a schedule.
      longer_than_usual: (ratio_vs_own_avg_distance !== null && ratio_vs_own_avg_distance > 1)
        || (ratio_vs_own_avg_days !== null && ratio_vs_own_avg_days > 1),
    };
  }).sort((a, b) => b.count - a.count);

  const lastEvent = services.length ? services.reduce((m, s) => (s.ts > m.ts ? s : m), services[0]) : null;
  return {
    total_events: services.length,
    total_spend: round2(services.reduce((a, s) => a + s.cost, 0)),
    intervals,
    last_event: lastEvent ? (() => {
      const date = dayOf(lastEvent.ts);
      return {
        kind: lastEvent.kind, date, odometer: lastEvent.odometer,
        days_since: /^\d{4}-\d{2}-\d{2}$/.test(date) ? daysBetween(date, today) : 0,
      };
    })() : null,
  };
}

/** Mirror of signals.rs::build_spend — per service category, last 12mo vs prior 12mo. */
function buildSpendByCategory(services: Service[], today: string) {
  const recentCut = shiftDays(today, RECENT_DAYS);
  const priorCut = shiftDays(today, PRIOR_DAYS);
  const acc = new Map<string, { recent: number; prior: number; count: number }>();
  for (const s of services) {
    const d = dayOf(s.ts);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    const e = acc.get(s.kind) ?? { recent: 0, prior: 0, count: 0 };
    if (d >= recentCut) { e.recent += s.cost; e.count += 1; }
    else if (d >= priorCut) { e.prior += s.cost; }
    acc.set(s.kind, e);
  }
  return [...acc.entries()]
    .filter(([, e]) => e.recent > 0 || e.prior > 0)
    .map(([kind, e]) => ({ kind, recent: round2(e.recent), prior: round2(e.prior), delta: round2(e.recent - e.prior), count_recent: e.count }))
    .sort((a, b) => b.recent - a.recent)
    .slice(0, 10);
}

/** Mirror of signals.rs::build_spend_summary — fuel (from measured segments) + service. */
function buildSpendSummary(
  fills: Fillup[], _points: EconomyPoint[], services: Service[],
  overall: { days_tracked: number }, today: string,
) {
  const recentCut = shiftDays(today, RECENT_DAYS);
  const priorCut = shiftDays(today, PRIOR_DAYS);
  // MONEY sums over EVERY fill — not over measured economy segments, which always exclude the
  // baseline fill (measured live 2026-08-19: two fills + one service showed "Month 151" and
  // "104 lifetime" on the SAME screen, and the AI told the customer their fuel spend was one
  // fill short). Economy/cost-per-mile stay segment-based — that part was right.
  let fuelLife = 0, fuelRecent = 0, totalRecent = 0, totalPrior = 0;
  for (const f of fills) {
    fuelLife += f.cost;
    const d = dayOf(f.ts);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    if (d >= recentCut) { fuelRecent += f.cost; totalRecent += f.cost; }
    else if (d >= priorCut) { totalPrior += f.cost; }
  }
  let svcLife = 0, svcRecent = 0;
  for (const s of services) {
    svcLife += s.cost;
    const d = dayOf(s.ts);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    if (d >= recentCut) { svcRecent += s.cost; totalRecent += s.cost; }
    else if (d >= priorCut) { totalPrior += s.cost; }
  }
  let pct: number | null = null, direction: string | null = null;
  if (totalPrior > 0) {
    pct = round2(((totalRecent - totalPrior) / totalPrior) * 100);
    direction = pct > 2 ? "up" : pct < -2 ? "down" : "flat";
  }
  const months = overall.days_tracked / 30.4375;
  return {
    fuel_lifetime: round2(fuelLife),
    service_lifetime: round2(svcLife),
    total_lifetime: round2(fuelLife + svcLife),
    fuel_recent_12mo: round2(fuelRecent),
    service_recent_12mo: round2(svcRecent),
    total_recent_12mo: round2(totalRecent),
    total_prior_12mo: round2(totalPrior),
    total_delta_12mo: round2(totalRecent - totalPrior),
    total_pct_change_12mo: pct,
    direction,
    avg_per_month: overall.days_tracked >= 30 && months > 0 ? round2((fuelLife + svcLife) / months) : null,
  };
}

/** Mirror of signals.rs::build_projection — needs a real span, else null rather than a guess. */
function buildProjection(overall: { distance_tracked: number | null; days_tracked: number; latest_odometer: number | null }) {
  const dist = overall.distance_tracked;
  if (dist === null || overall.days_tracked < 14 || dist <= 0) return null;
  const perDay = dist / overall.days_tracked;
  return {
    avg_distance_per_day: round2(perDay),
    avg_distance_per_month: round2(perDay * 30.4375),
    projected_distance_next_12mo: round2(perDay * 365),
    projected_odometer_in_12mo: round2((overall.latest_odometer ?? 0) + perDay * 365),
  };
}

/**
 * Mirror of signals.rs::build_headline_facts.
 * ⚠️ Every sentence is a FACT about the owner's own log. Note what fact 4 does NOT say: it reports
 * "N since your last X — about 1.4× your own usual gap", never "your X is due".
 */
function buildHeadlineFacts(
  u: { economy: string; distance: string },
  overall: ReturnType<typeof computeSignalsWeb> extends never ? never : { fillups: number; days_tracked: number; distance_tracked: number | null },
  economy: ReturnType<typeof buildEconomy>,
  cpd: ReturnType<typeof buildCpd>,
  service: ReturnType<typeof buildServiceStats>,
  spendByCategory: ReturnType<typeof buildSpendByCategory>,
  spend: ReturnType<typeof buildSpendSummary>,
  projection: ReturnType<typeof buildProjection>,
) {
  const facts: { kind: string; text: string; tone: string }[] = [];

  // 1) Fuel-economy trend.
  if (economy.recent_avg !== null && economy.direction !== null) {
    if (economy.prior_avg !== null && economy.delta !== null && economy.direction !== "flat") {
      const verb = economy.direction === "improving" ? "up" : "down";
      facts.push({
        kind: "economy",
        text: `Your recent fuel economy is ${economy.recent_avg.toFixed(1)} ${u.economy} — ${verb} ${Math.abs(economy.delta).toFixed(1)} from the prior stretch.`,
        tone: economy.direction === "improving" ? "good" : "watch",
      });
    } else {
      facts.push({
        kind: "economy",
        text: `Your recent fuel economy is ${economy.recent_avg.toFixed(1)} ${u.economy}, holding steady.`,
        tone: "neutral",
      });
    }
  }

  // 1b) Recent vs the owner's OWN lifetime norm.
  if (economy.recent_vs_lifetime_pct !== null && economy.prior_avg !== null) {
    const off = economy.recent_vs_lifetime_pct - 100;
    if (Math.abs(off) >= 2) {
      facts.push({
        kind: "economy",
        text: `That's running about ${Math.abs(off).toFixed(0)}% ${off > 0 ? "better than" : "below"} your own lifetime average of ${(economy.lifetime_avg ?? 0).toFixed(1)} ${u.economy}.`,
        tone: off > 0 ? "good" : "watch",
      });
    }
  }

  // 1c) How far off their own best-ever segment.
  if (economy.off_best_pct !== null && economy.best !== null && economy.off_best_pct >= 3) {
    facts.push({
      kind: "economy",
      text: `Your recent average is about ${economy.off_best_pct.toFixed(0)}% off your best-ever ${economy.best.toFixed(1)} ${u.economy} segment.`,
      tone: "watch",
    });
  }

  // 2) Cost per distance.
  if (cpd.recent !== null && cpd.pct_change !== null && cpd.direction !== null) {
    if (cpd.direction !== "flat") {
      facts.push({
        kind: "cost",
        text: `Fuel is costing ${cpd.recent.toFixed(2)} per ${u.distance} lately — ${cpd.direction === "up" ? "risen" : "fallen"} ${Math.abs(cpd.pct_change).toFixed(0)}% versus the prior 12 months.`,
        tone: cpd.direction === "up" ? "watch" : "good",
      });
    } else {
      facts.push({
        kind: "cost",
        text: `Fuel is costing about ${cpd.recent.toFixed(2)} per ${u.distance} lately, roughly flat year-on-year.`,
        tone: "neutral",
      });
    }
  }

  // 3) Last logged service.
  if (service.last_event) {
    facts.push({
      kind: "service",
      text: `Your last logged service was ${prettyKind(service.last_event.kind)} on ${service.last_event.date}.`,
      tone: "neutral",
    });
  }

  // 4) Categories running longer than the owner's OWN usual gap — a RATIO, never "due".
  for (const iv of service.intervals) {
    if (!iv.longer_than_usual) continue;
    if (iv.distance_since_last !== null && iv.avg_distance_between !== null && iv.ratio_vs_own_avg_distance !== null) {
      facts.push({
        kind: "interval",
        text: `It's been ${fmt0(iv.distance_since_last)} ${u.distance} since your last ${prettyKind(iv.kind)} — about ${iv.ratio_vs_own_avg_distance.toFixed(1)}× your own usual ${fmt0(iv.avg_distance_between)}-${u.distance} gap.`,
        tone: "watch",
      });
    } else if (iv.ratio_vs_own_avg_days !== null && iv.avg_days_between !== null) {
      facts.push({
        kind: "interval",
        text: `It's been ${iv.days_since_last} days since your last ${prettyKind(iv.kind)} — about ${iv.ratio_vs_own_avg_days.toFixed(1)}× your own usual ${fmt0(iv.avg_days_between)}-day gap.`,
        tone: "watch",
      });
    }
    if (facts.filter((f) => f.kind === "interval").length >= 2) break;
  }

  // 4b) Otherwise their own typical cadence for the most-serviced category.
  if (!facts.some((f) => f.kind === "interval")) {
    const iv = service.intervals.find((i) => i.count >= 3 && i.avg_distance_between !== null);
    if (iv && (iv.avg_distance_between as number) > 0) {
      facts.push({
        kind: "interval",
        text: `On your record you log ${prettyKind(iv.kind)} about every ${fmt0(iv.avg_distance_between as number)} ${u.distance} (${iv.count} times so far).`,
        tone: "neutral",
      });
    }
  }

  // 5) Spend, last 12 months vs the year before.
  if (spend.total_pct_change_12mo !== null && spend.direction !== null) {
    if (spend.direction !== "flat") {
      facts.push({
        kind: "spend",
        text: `You've spent ${fmt0(spend.total_recent_12mo)} on this vehicle in the last 12 months — ${spend.direction} ${Math.abs(spend.total_pct_change_12mo).toFixed(0)}% from the year before.`,
        tone: spend.direction === "up" ? "watch" : "good",
      });
    }
  } else if (spend.total_lifetime > 0) {
    facts.push({ kind: "spend", text: `You've logged ${fmt0(spend.total_lifetime)} in total on this vehicle so far.`, tone: "neutral" });
  }

  // 5b) Biggest service line in the last 12 months — only when there's more than one line.
  const withRecent = spendByCategory.filter((c) => c.recent > 0);
  if (withRecent.length >= 2) {
    const top = withRecent.reduce((a, b) => (b.recent > a.recent ? b : a));
    facts.push({
      kind: "spend",
      text: `Your biggest service line in the last 12 months was ${prettyKind(top.kind)} at ${fmt0(top.recent)}.`,
      tone: "neutral",
    });
  }

  // 6) Mileage cadence.
  if (projection) {
    facts.push({
      kind: "mileage",
      text: `You're averaging about ${fmt0(projection.avg_distance_per_month)} ${u.distance}/month; at that pace the odometer reaches ~${fmt0(projection.projected_odometer_in_12mo)} ${u.distance} in a year.`,
      tone: "neutral",
    });
  }

  // 7) Tracked-distance milestone.
  if (overall.distance_tracked !== null && overall.distance_tracked > 0) {
    facts.push({
      kind: "milestone",
      text: `You've tracked ${fmt0(overall.distance_tracked)} ${u.distance} across ${overall.fillups} fill-ups over ${overall.days_tracked} days.`,
      tone: "neutral",
    });
  }

  return facts;
}
